import { SettingsPermissions } from "@redotech/account-common/permissions";
import { Payer } from "@redotech/congo-enums/payer";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import type { AdjustmentAmounts } from "@redotech/redo-return-core-common/return-flow/adjustment-unit";
import { ReturnOptionMethod } from "@redotech/redo-return-core-common/return-flow/return-option";
import { SubmitWarrantyState } from "@redotech/redo-return-ui/flows/return-flow/submit-warranty";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Text } from "@redotech/redo-web/text";
import { produce } from "immer";
import { memo, useMemo } from "react";
import { AdjustmentElement, returnOptionMethods } from "./return";

function translatedReturnOptionLabel(
  method: ReturnOptionMethod["type"],
  t: (key: string) => string,
): string {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (method) {
    case ReturnOptionMethod.EXCHANGE:
      return t("Exchange");
    case ReturnOptionMethod.REFUND:
      return t("Refund");
    case ReturnOptionMethod.STORE_CREDIT:
      return t("Store credit");
  }
  return t("Unknown");
}

export const SubmitWarrantyDetails = memo(function SubmitWarrantyDetails({
  state,
  setState,
}: StepTypeDetailsProps<SubmitWarrantyState>) {
  const { t } = useTranslation();
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );

  const returnOptionMethodsListItems: RedoListItem<
    ReturnOptionMethod["type"]
  >[] = useMemo(
    () =>
      returnOptionMethods.map((method) => ({
        id: method,
        value: method,
        type: "text" as const,
        text: translatedReturnOptionLabel(method, t),
      })),
    [t],
  );

  const handleActionChange = useHandler((method: ReturnOptionMethod["type"]) =>
    setState((state) =>
      produce(state, (state) => {
        state.option.method = method;
      }),
    ),
  );
  const handleRequireSendBack = useHandler((value: boolean) =>
    setState((state) =>
      produce(state, (state) => {
        state.option.requireSendBack = value;
      }),
    ),
  );
  const handleSafetyNet = useHandler((value: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.option.labelPayer = value ? Payer.Merchant : Payer.Customer;
      }),
    );
  });
  const handleManualLabel = useHandler((value: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.option.manual_label = value;
        if (value) {
          state.option.requireSendBack = true;
          state.option.labelPayer = Payer.Customer;
        }
      }),
    );
  });
  const handleAmountsChange = useHandler((next: AdjustmentAmounts) => {
    setState((state) =>
      produce(state, (state) => {
        state.option.adjustmentFlat = next.adjustmentFlat;
        state.option.adjustmentPercent = next.adjustmentPercent;
        state.option.perCurrencyAmountsEnabled = next.perCurrencyAmountsEnabled;
      }),
    );
  });
  const handleAdjustmentFlat = useHandler((value: string) => {
    setState((state) =>
      produce(state, (state) => {
        state.option.adjustmentFlat = value;
      }),
    );
  });
  const handleAdjustmentPercent = useHandler((value: string) => {
    setState((state) =>
      produce(state, (state) => {
        state.option.adjustmentPercent = value;
      }),
    );
  });
  const handleCurrencyOverrides = useHandler(
    (overrides: Record<string, string>) => {
      setState((state) =>
        produce(state, (state) => {
          state.option.currencyOverrides = overrides;
        }),
      );
    },
  );
  const handlePerCurrencyAmountsEnabled = useHandler((enabled: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.option.perCurrencyAmountsEnabled = enabled;
      }),
    );
  });
  const handleDiscountTitle = useHandler((value: string) => {
    setState((state) =>
      produce(state, (state) => {
        state.option.discountTitle = value;
      }),
    );
  });
  const handleChargeCustomerForNewOrderShipping = useHandler(
    (value: boolean) => {
      setState((state) =>
        produce(state, (state) => {
          state.option.chargeCustomerForNewOrderShipping = value;
        }),
      );
    },
  );
  const handleCarryOverLineItemProperties = useHandler((value: boolean) =>
    setState((state) =>
      produce(state, (state) => {
        state.option.carryOverLineItemProperties = value;
      }),
    ),
  );
  const handleAllowUnpublishedReplacement = useHandler((value: boolean) =>
    setState((state) =>
      produce(state, (state) => {
        state.option.allowUnpublishedReplacement = value;
      }),
    ),
  );
  return (
    <>
      <Text fontSize="xs" textColor="secondary">
        {t("Compensation Method")}
      </Text>
      <RedoSingleSelectDropdownInput
        disabled={!canEditReturnsSettings}
        options={returnOptionMethodsListItems}
        optionSelected={({ item }) => handleActionChange(item.value)}
        selectedItem={returnOptionMethodsListItems.find(
          (item) => item.value === state.option.method,
        )}
        size="xs"
      />
      <AdjustmentElement
        adjustmentFlat={state.option.adjustmentFlat ?? "0"}
        adjustmentPercent={state.option.adjustmentPercent ?? "0"}
        canEditReturnsSettings={canEditReturnsSettings}
        currencyOverrides={state.option.currencyOverrides}
        handleAdjustmentFlat={handleAdjustmentFlat}
        handleAdjustmentPercent={handleAdjustmentPercent}
        handleAmountsChange={handleAmountsChange}
        handleCurrencyOverridesChange={handleCurrencyOverrides}
        handlePerCurrencyAmountsEnabledChange={handlePerCurrencyAmountsEnabled}
        perCurrencyAmountsEnabled={state.option.perCurrencyAmountsEnabled}
      />
      <RedoToggle
        disabled={!canEditReturnsSettings}
        label={t("Require customer to send back item?")}
        setValue={handleRequireSendBack}
        value={state.option.requireSendBack || false}
      />
      <RedoToggle
        description={t(
          "Merchant pays for labels not covered by {{brandName}}.",
          { brandName: "Redo" },
        )}
        disabled={!canEditReturnsSettings}
        label={t("Safety net")}
        setValue={handleSafetyNet}
        value={state.option.labelPayer === Payer.Merchant}
      />
      <RedoToggle
        description={t("Merchant will handle the shipping label")}
        disabled={!canEditReturnsSettings}
        label={t("Manual Label")}
        setValue={handleManualLabel}
        value={state.option.manual_label ?? false}
      />
      {state.option.method === ReturnOptionMethod.EXCHANGE && (
        <>
          <RedoTextInput
            description={t("Title applied to the discount on the draft order.")}
            label={t("Discount title")}
            setValue={handleDiscountTitle}
            state={canEditReturnsSettings ? "default" : "disabled"}
            value={state.option.discountTitle ?? ""}
          />
          <RedoToggle
            description={t(
              "Customer pays for shipping on the new exchange order.",
            )}
            disabled={!canEditReturnsSettings}
            label={t("Charge customer for new order shipping")}
            setValue={handleChargeCustomerForNewOrderShipping}
            value={state.option.chargeCustomerForNewOrderShipping ?? false}
          />
          <RedoToggle
            description={t(
              "If enabled, line item properties (e.g. engravings, monograms) from the returned item are copied to the same-item or variant exchange item.",
            )}
            disabled={!canEditReturnsSettings}
            label={t("Carry over item properties to exchanges")}
            setValue={handleCarryOverLineItemProperties}
            value={state.option.carryOverLineItemProperties ?? true}
          />
          <RedoToggle
            description={t(
              "Grant the same-item replacement even if the product not currently active. Products that are deleted or inactive products when disabled fall back to store credit.",
            )}
            disabled={!canEditReturnsSettings}
            label={t("Allow replacement even if item is not published")}
            setValue={handleAllowUnpublishedReplacement}
            value={state.option.allowUnpublishedReplacement ?? false}
          />
        </>
      )}
    </>
  );
});
